import clsx from "clsx";
import { format, isSameDay, parseISO } from "date-fns";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import IconCross from "../../assets/icons/icon-cross.svg";
import {
  useAcuityAvailableDates,
  useAcuityAvailableTimes,
  useAcuityForms
} from "../../hooks/useAcuitySchedule";
import { useLocations } from "../../hooks/useLocations";

import AcuityFormInput from "../acuity-form-input";
import Button from "../button";
import Input from "../input";
import Select from "../select";
import DateSelect from "../date-select";

import styles from "./booking-form.module.scss";
import TagManager from "react-gtm-module";

function isDayDisabled(day, enabledDays) {
  return !enabledDays
    .map(({ date }) => parseISO(date))
    .some((enabledDay) => isSameDay(day, enabledDay));
}

// Membership field in Acuity to overwrite options
// with the ones from the selected location.
const membershipFieldId = 11264014;

const BookingForm = ({ title, location, hideDesktop = false }) => {
  const [error, setError] = useState("");
  const [showMobileForm, setShowMobileForm] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  const {
    register,
    formState,
    handleSubmit,
    watch,
    getValues,
    setValue,
    reset
  } = useForm({
    mode: "onChange",
    defaultValues: {
      calendarID: parseInt(location?.acuityCalendarId),
      tourDate: "",
      datetime: ""
    }
  });

  const { data: locations } = useLocations();
  const { data: dates } = useAcuityAvailableDates(getValues().calendarID);
  const { data: times } = useAcuityAvailableTimes(
    getValues().calendarID,
    getValues().tourDate
  );
  const { data: forms, loading: fieldsLoading } = useAcuityForms();
  const { isDirty } = formState;

  watch();

  useEffect(() => {
    if (forms && forms[0] && forms[0].fields && !isDirty) {
      forms[0].fields.map((field) => {
        setValue(`field:${field.id}`, "", {
          shouldDirty: false,
          shouldValidate: false,
          shouldTouch: false
        });
      });
    }
  }, [forms, isDirty, setValue]);

  const onSubmit = (data) => {
    setError("");
    const copyData = { ...data };
    const fields = Object.entries(copyData)
      .filter(([key]) => key.includes("field:"))
      .map(([key, value]) => {
        const fieldId = key.split(":")[1];
        delete copyData[key];
        const foundFormField = forms[0].fields?.find(
          (field) => field.id === parseInt(fieldId)
        );

        if (foundFormField?.type === "yesno") {
          value = value ? "yes" : "no";
        }
        return { id: fieldId, value };
      });

    copyData.fields = fields;

    const trackingData = {
      event: "book_tour",
      firstname: copyData.fields.firstName,
      lastname: copyData.fields.lastName,
      ...copyData.fields
    };

    delete trackingData.firstName;
    delete trackingData.lastName;

    TagManager.dataLayer({
      dataLayer: trackingData
    });

    fetch("/api/appointment", {
      method: "POST",
      body: JSON.stringify(copyData)
    })
      .then(async (res) => {
        if (res.status !== 200) {
          const errResponse = await res.json();
          switch (errResponse.error) {
            case "not_available":
              setError("Sorry, that time is no longer available.");
              return;
          }
          setError(errResponse.message);
          return;
        }

        setShowConfirmation(true);
        reset();
      })
      .catch(() => setError("Something went wrong. Please try again."));
  };

  const onError = (e) => console.log(e);

  const currentLocation = locations.find(
    (loc) => loc.acuityCalendarId === getValues().calendarID
  );

  if (fieldsLoading) return null;

  const { errors } = formState;

  if (!dates) {
    return null;
  }

  return (
    <>
      <form
        className={clsx(
          "p-6",
          styles.FormContainer,
          showMobileForm && styles.MobileOpen,
          hideDesktop && styles.HideDesktop
        )}
        onSubmit={handleSubmit(onSubmit, onError)}
      >
        <div
          className={clsx(
            styles.FormTransition,
            showConfirmation && styles.Collapse
          )}
        >
          {showConfirmation && (
            <>
              <h2 className="font-bold text-2xl text-center">
                You&apos;re all set.
              </h2>

              <p className="text-md my-4 text-center font-light">
                We look forward to showing you our space! A confirmation email
                will be sent shortly.
              </p>

              <Button
                className={clsx(styles.CloseButton)}
                clear
                type="button"
                onClick={() => {
                  reset();
                  setShowConfirmation(false);
                }}
              >
                <IconCross width="17" />
              </Button>
            </>
          )}
          {!showConfirmation && (
            <>
              <h2 className="font-bold text-2xl">{title}</h2>

              <div className="mt-4">
                <div className="form-group-inline">
                  <Input
                    type="text"
                    placeholder="First name *"
                    {...register("firstName", {
                      required: "First name is required"
                    })}
                    errorText={errors.firstName && errors.firstName.message}
                    inline
                  />
                  <Input
                    type="text"
                    placeholder="Last name *"
                    {...register("lastName", {
                      required: "Last name is required"
                    })}
                    errorText={errors.lastName && errors.lastName.message}
                    inline
                  />
                </div>

                <Input
                  type="text"
                  placeholder="Work email address *"
                  {...register("email", { required: "Email is required" })}
                  errorText={errors.email && errors.email.message}
                />

                <Input
                  type="text"
                  placeholder="Phone number *"
                  {...register("phone", {
                    required: "Phone number is required"
                  })}
                  errorText={errors.phone && errors.phone.message}
                />
              </div>

              <div>
                {!location && (
                  <Select
                    {...register("calendarID", {
                      required: "Location is required"
                    })}
                    errorText={errors.calendarID && errors.calendarID.message}
                  >
                    <option value="" disabled>
                      Select your location of interest *
                    </option>
                    {locations.map((location) => (
                      <option
                        value={location.acuityCalendarId}
                        key={location.slug}
                      >
                        {location.name}
                      </option>
                    ))}
                  </Select>
                )}

                <div className="form-group-inline">
                  <DateSelect
                    placeholder="Tour date *"
                    selectedDate={getValues().tourDate}
                    disabledDates={(day) => isDayDisabled(day, dates)}
                    {...register("tourDate", {
                      required: "Tour date is required"
                    })}
                    onChange={(day) => {
                      setValue("tourDate", day.toISOString(), {
                        shouldValidate: true
                      });
                    }}
                    inline
                    errorText={errors.tourDate && errors.tourDate.message}
                  />
                  <Select
                    type="text"
                    placeholder="Tour time *"
                    {...register("datetime", {
                      required: "Tour time is required"
                    })}
                    errorText={errors.datetime && errors.datetime.message}
                    inline
                  >
                    <option value="" disabled>
                      Tour time *
                    </option>
                    {times?.map(({ time }) => (
                      <option key={time} value={time}>
                        {format(new Date(time), "h:mm aa")}
                      </option>
                    ))}
                  </Select>
                </div>

                {forms[0]?.fields?.map((field) => {
                  // check form calendar id matches selected location

                  if (currentLocation && field.id === membershipFieldId) {
                    // Override the membership field to show location specific memberships

                    let allOptions = [];
                    for (let solution of currentLocation
                      .locationSolutionsCollection.items) {
                      allOptions = [
                        ...allOptions,
                        ...solution.optionsCollection.items
                      ];
                    }

                    field.options = allOptions.map((opt) => opt.name);

                    return (
                      <AcuityFormInput
                        key={field.id}
                        field={field}
                        {...register(`field:${field.id}`, {
                          required: field.required && "This field is required"
                        })}
                        required={field.required}
                        errorText={
                          errors[`field:${field.id}`] &&
                          errors[`field:${field.id}`].message
                        }
                      />
                    );
                  }
                  return (
                    <AcuityFormInput
                      key={field.id}
                      field={field}
                      {...register(`field:${field.id}`, {
                        required: field.required && "This field is required"
                      })}
                      required={field.required}
                      errorText={
                        errors[`field:${field.id}`] &&
                        errors[`field:${field.id}`].message
                      }
                    />
                  );
                })}
              </div>

              <div className={styles.DesktopButton}>
                {error && <p>{error}</p>}

                <Button type="submit" className="w-full my-4" outline>
                  Book
                </Button>
              </div>
            </>
          )}

          {/* Mobile specific buttons to toggle this view on and off. */}
          <div className={styles.MobileControls}>
            {!showMobileForm && (
              <>
                <Button
                  type="button"
                  className="w-full my-2"
                  outline
                  onClick={() => setShowMobileForm(true)}
                >
                  Book a Tour
                </Button>
              </>
            )}

            {showMobileForm && !showConfirmation && (
              <>
                <Button
                  type="submit"
                  className="w-full my-2"
                  outline
                  onClick={() => setShowMobileForm(true)}
                >
                  Book
                </Button>

                <Button
                  type="button"
                  className="w-full my-2"
                  outline
                  onClick={() => {
                    reset();
                    setShowMobileForm(false);
                  }}
                >
                  Cancel
                </Button>
              </>
            )}

            {showMobileForm && showConfirmation && (
              <>
                <Button
                  type="button"
                  className="w-full my-2"
                  outline
                  onClick={() => {
                    reset();
                    setShowMobileForm(false);
                  }}
                >
                  Close
                </Button>
              </>
            )}
          </div>
        </div>
      </form>
    </>
  );
};

export default BookingForm;
